/**
 * Утро года (core-loop-v2 §2, такт 1): «Год N · возраст M», дельты
 * состояний и денег за прошлый год («здоровье +3», «деньги −120 $»),
 * записи прошлого года (тяжёлые — с блоком помощи), лента фокуса на этот
 * год и кнопка «К делам». В первой жизни здесь же три подсказки хода
 * (энергия, места, следующий год) — закрываются одной кнопкой и ставят
 * `settings.hintsSeen`.
 *
 * Дельты берутся из `game.lastDeltas` (null в первый год и после загрузки —
 * тогда список не показывается); записи — из журнала по возрасту прошлого
 * года (`lastYearEntries`), так что в них есть и дела игрока, и тик.
 */
import { t } from '@life/i18n';
import { HELP_KEY } from '@life/engine';
import type { Game } from '../state/game';
import { lastYearEntries, STAT_KEYS } from '../state/turn';
import { FocusRibbon } from './FocusRibbon';
import { HelpBlock } from './HelpBlock';

/** Подсказки первой жизни в утренней карточке — три и только три (D1). */
export const TURN_HINTS: readonly string[] = ['ui.hint.turn.energy', 'ui.hint.turn.places', 'ui.hint.turn.next'];

function signed(n: number): string {
  return n > 0 ? `+${n}` : String(n);
}

export function MorningCard({ game }: { game: Game }) {
  const life = game.life.value;
  if (life === null) return null;
  const deltas = game.lastDeltas.value;
  const entries = lastYearEntries(life).filter((e) => e.key !== HELP_KEY);
  const hints = !game.settings.value.hintsSeen;
  const lines: { key: string; text: string }[] = [];
  if (deltas) {
    for (const k of STAT_KEYS) {
      if (deltas.stats[k] !== 0) lines.push({ key: k, text: t('ui.morning.delta', { name: t(`ui.stats.${k}`), delta: signed(deltas.stats[k]) }) });
    }
    if (deltas.money !== 0) lines.push({ key: 'money', text: t('ui.morning.deltaMoney', { name: t('ui.stats.money'), delta: signed(deltas.money) }) });
  }

  return (
    <section class="morning" aria-live="polite">
      <div class="eyebrow">{t('ui.morning.eyebrow')}</div>
      <h2 class="t">{t('ui.morning.title', { year: life.birthYear + life.age, age: life.age })}</h2>
      {deltas && (
        <ul class="deltas">
          {lines.length === 0 ? (
            <li class="mono">{t('ui.morning.noChange')}</li>
          ) : (
            lines.map((l) => (
              <li key={l.key} class="mono">
                {l.text}
              </li>
            ))
          )}
        </ul>
      )}
      {entries.length > 0 && (
        <ul class="morning-log">
          {entries.map((e, i) => (
            <li key={i} class={e.heavy ? 'heavy' : ''}>
              <span>{t(e.key, e.params)}</span>
              {e.heavy && <HelpBlock />}
            </li>
          ))}
        </ul>
      )}
      {hints && (
        <div class="alert info hint-card" role="status">
          {TURN_HINTS.map((key) => (
            <p key={key}>{t(key)}</p>
          ))}
          <button type="button" class="btn" onClick={() => (game.settings.value = { ...game.settings.value, hintsSeen: true })}>
            {t('ui.hint.close')}
          </button>
        </div>
      )}
      <div class="focus-line">
        <span class="eyebrow">{t('ui.life.focus')}</span>
      </div>
      <FocusRibbon life={life} focus={game.focus.value} focusChanges={game.focusChanges.value} onFocus={(f) => game.setFocus(f)} />
      <div class="actions">
        <button type="button" class="btn primary block" onClick={() => game.startActions()}>
          {t('ui.morning.go')}
        </button>
      </div>
    </section>
  );
}
